import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import theme from '../../config/theme'
import Layout from '../../layout/layout'
import Input from '../../common/input'
import Table from '../../common/table'
import Pagination from '../../common/pagination'
import { IoEyeOutline, IoDownloadOutline } from 'react-icons/io5'
import { PCSegmentDataDto } from '../../dtos/pcSegment.dto'
import { segmentsApi } from '../../utils/apiClient'

const PCSegmentPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20) // Match API default size

  // API data states
  const [segments, setSegments] = useState<PCSegmentDataDto[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  // Fetch segments from API
  const fetchSegments = useCallback(async (name?: string, page: number = 0) => {
    setIsLoading(true)
    setError(null)

    try {
      // Convert UI page (1-based) to API page (0-based)
      const apiPage = page - 1

      const response = await segmentsApi.searchSegments(
        name?.trim() || undefined,
        apiPage,
        itemsPerPage
      )

      setSegments(response.data || [])
      setTotalItems(response.meta?.totalItems || 0)
      setTotalPages(response.meta?.totalPages || 0)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch segments'
      setError(errorMessage)
      setSegments([])
      setTotalItems(0)
      setTotalPages(0)
    } finally {
      setIsLoading(false)
    }
  }, [itemsPerPage])

  // Search query state for debounced fetching
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')

  // Debounced search - update debouncedSearchQuery after user stops typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
      // Reset to page 1 when search changes
      setCurrentPage(1)
    }, 500) // 500ms debounce

    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  // Fetch segments when page or debounced search changes
  useEffect(() => {
    fetchSegments(debouncedSearchQuery, currentPage)
  }, [currentPage, debouncedSearchQuery, fetchSegments])

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // Handle view
  const handleView = (id: string | number) => {
    navigate(`/pc-segment/${id}`)
  }

  // Handle export
  const handleExport = async (id: string | number) => {
    try {
      // Find segment to get name for filename
      const segment = segments.find(item => item.id === id || String(item.id) === String(id))
      const segmentName = segment?.segmentName || segment?.employeeName || 'segment'
      
      // Call export API
      const blob = await segmentsApi.exportSegment(id)

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // Generate filename with segment name and current date
      const now = new Date()
      const dateStr = now.toISOString().split('T')[0] // YYYY-MM-DD
      const safeSegmentName = segmentName.replace(/[^a-z0-9]/gi, '_').toLowerCase()
      link.download = `${safeSegmentName}_${dateStr}.csv`

      // Trigger download
      document.body.appendChild(link)
      link.click()
      
      // Cleanup
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to export segment'
      setError(errorMessage)
      // Clear error after 5 seconds
      setTimeout(() => setError(null), 5000)
    }
  }

  // Table headers
  const header = ['Segment Name', 'Amount of PC', 'Action']

  // Render table row
  const renderRow = (item: PCSegmentDataDto) => {
    return (
      <>
        {/* Segment Name */}
        <td className="px-6 py-4 whitespace-nowrap">
          <span
            className="text-sm font-medium"
            style={{ color: theme.color.gray500 }}
          >
            {item.segmentName || item.employeeName || ''}
          </span>
        </td>
        {/* Amount of PC */}
        <td className="px-6 py-4 whitespace-nowrap">
          <span
            className="text-sm"
            style={{ color: theme.color.gray500 }}
          >
            {item.amountOfPc ?? item.amountOfPC ?? 0}
          </span>
        </td>
        {/* Action */}
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center space-x-2">
            {/* View Button */}
            <button
              onClick={() => handleView(Number(item.id))}
              className="flex items-center px-3 py-1.5 rounded-lg border transition-colors hover:shadow-sm"
              style={{
                backgroundColor: theme.color.light100,
                borderColor: theme.color.light500,
                color: theme.color.gray500
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = theme.color.light400
                e.currentTarget.style.backgroundColor = theme.color.light200
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = theme.color.light500
                e.currentTarget.style.backgroundColor = theme.color.light100
              }}
            >
              {React.createElement(IoEyeOutline as any, {
                className: "w-5 h-5 mr-2 font-medium"
              })}
              <span className="text-sm font-medium">View</span>
            </button>
            {/* Export Button */}
            <button
              onClick={() => handleExport(item?.id)}
              className="flex items-center px-3 py-1.5 rounded-lg border transition-colors hover:shadow-sm"
              style={{
                backgroundColor: theme.color.light100,
                borderColor: theme.color.light500,
                color: theme.color.gray500
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = theme.color.light400
                e.currentTarget.style.backgroundColor = theme.color.light200
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = theme.color.light500
                e.currentTarget.style.backgroundColor = theme.color.light100
              }}
            >
              {React.createElement(IoDownloadOutline as any, {
                className: "w-5 h-5 mr-2 font-medium"
              })}
              <span className="text-sm font-medium">Export</span>
            </button>
          </div>
        </td>
      </>
    )
  }

  return (
    <Layout currentPath="/pc-segment">
      <div className="flex flex-col overflow-hidden">
        {/* Page Header */}
        <div className="mb-6">
          <h1
            className="text-2xl font-medium mb-6"
            style={{ color: theme.color.gray500 }}
          >
            PC Segments
          </h1>

          {/* Search Bar */}
          <div className="relative" style={{ width: '100%' }}>
            <Input
              type="text"
              placeholder="Search by Segment name"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                // Page reset will be handled by debounce effect
              }}
              className="!pl-12"
            />
            <div
              className="absolute left-4"
              style={{
                top: '50%',
                transform: 'translateY(-50%)',
                color: theme.color.gray200,
                pointerEvents: 'none'
              }}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            className="mb-4 p-4 rounded-lg text-sm"
            style={{
              backgroundColor: `${theme.color.error}20`,
              borderColor: `${theme.color.error}50`,
              color: theme.color.error,
              border: '1px solid'
            }}
          >
            {error}
          </div>
        )}

        {/* Table Container */}
        <div className="flex flex-col flex-1 overflow-hidden">
          <div className="max-h-[calc(100vh-295px)] flex flex-col relative">
            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: theme.brandColor.red }}></div>
              </div>
            )}

            {/* Table */}
            {!isLoading && (
              <Table
                data={segments}
                className={`flex-1 ${totalPages > 1 ? 'pb-0' : ''}`}
                header={header}
                renderRow={renderRow}
              />
            )}

            {/* Pagination - Fixed at bottom */}
            {!isLoading && totalPages > 0 && (
              <div className="fixed bottom-0 left-64 right-0 mx-6 py-4 z-10">
                <div className="flex justify-center">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    itemsPerPage={itemsPerPage}
                    totalItems={totalItems}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default PCSegmentPage

